import { inject, injectable } from '@wix/typesafe-inversify';
import { Services } from '../../../ioc/services';
import type { IReadyAppData } from '../../ReadyAppData/IReadyAppData';
import type { IEditorSDK } from '../../../types/PlatformTypes';
import type { IConsoleLogger } from '../../ConsoleLogger/IConsoleLogger';
import type {
  ICommonSessionData,
  ICommonSessionDataProvider,
  IEditorTraceData,
} from './ICommonSessionDataProvider';

@injectable()
export class CommonSessionDataProvider implements ICommonSessionDataProvider {
  constructor(
    @inject(Services.ReadyAppData)
    private readonly readyAppDataService: IReadyAppData,
    @inject(Services.EditorSDK)
    private readonly editorSdk: IEditorSDK,
    @inject(Services.ConsoleLogger)
    private readonly consoleLogger: IConsoleLogger,
  ) {}

  getCommonSessionData(): ICommonSessionData {
    const { userId, editorSessionId, appDefinitionId } =
      this.readyAppDataService.editorReadyOptions.initialAppData;

    return {
      userId,
      editorSessionId,
      appDefinitionId,
    };
  }

  async getEditorTraceData(): Promise<IEditorTraceData> {
    let timeoutId: ReturnType<typeof setTimeout>;
    return Promise.race([
      this.getEditorDataImpl()
        .catch((error) => {
          this.consoleLogger.error(error);
          return {};
        })
        .finally(() => clearTimeout(timeoutId)),
      new Promise<IEditorTraceData>((resolve, reject) => {
        timeoutId = setTimeout(() => {
          console.info('Timed out for ');
          resolve({});
        }, 5000);
      }),
    ]);
  }

  private async getEditorDataImpl(): Promise<IEditorTraceData> {
    const pageRef = await this.editorSdk.pages.getCurrent('token');
    const [pageData, userInfo] = await Promise.all([
      this.editorSdk.pages.getPageData('token', {
        pageRef,
      }),
      this.editorSdk.info.getUserInfo('token'),
    ]);

    return {
      user: {
        id: userInfo.userId,
        name: userInfo.name,
        accountId: userInfo.userId,
        isWixUser: userInfo.email?.endsWith('@wix.com'),
      },
      page: {
        pageTitle: pageData.title,
        pageId: pageData.id,
      },
    };
  }
}
